import { useState, useEffect } from 'react'
import {
  Search, FileText, Download, CheckCircle, XCircle,
  Clock, Eye, ChevronDown, ChevronUp, X, AlertTriangle
} from 'lucide-react'
import { fileService } from '../services/fileService'

const FILE_TYPE_LABELS = {
  isi: 'ISI',
  cover: 'COVER',
  cover_custom: 'COVER CUSTOM',
  sisipan: 'SISIPAN',
}

const FILE_TYPE_COLORS = {
  isi: 'bg-blue-100 text-blue-700',
  cover: 'bg-red-100 text-red-700',
  cover_custom: 'bg-purple-100 text-purple-700',
  sisipan: 'bg-orange-100 text-orange-700',
}

const PRIORITY_STYLES = {
  urgent: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  normal: 'bg-blue-100 text-blue-700 border-blue-200',
  low: 'bg-gray-100 text-gray-700 border-gray-200',
}

function ReviewerPage() {
  const [activeTab, setActiveTab] = useState('pending')
  const [groups, setGroups] = useState([])
  const [totalFiles, setTotalFiles] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')

  // Review modal state
  const [reviewFile, setReviewFile] = useState(null)
  const [showReviewModal, setShowReviewModal] = useState(false)

  // Version history state
  const [versionFile, setVersionFile] = useState(null)
  const [versions, setVersions] = useState([])
  const [showVersionModal, setShowVersionModal] = useState(false)

  useEffect(() => {
    fetchFiles()
  }, [activeTab])

  const fetchFiles = async () => {
    try {
      setLoading(true)
      const res = await fileService.getPendingReview({ status: activeTab })
      if (res.success) {
        setGroups(res.data.groups || [])
        setTotalFiles(res.data.total || 0)
      }
    } catch (error) {
      console.error('Error fetching review files:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async (file) => {
    try {
      const response = await fileService.download(file.id)
      if (response.data instanceof Blob) {
        const url = window.URL.createObjectURL(response.data)
        const a = document.createElement('a')
        a.href = url
        a.download = file.filename
        a.click()
        window.URL.revokeObjectURL(url)
      }
    } catch (err) {
      console.error('Download error:', err)
    }
  }

  const openReviewModal = (file) => {
    setReviewFile(file)
    setShowReviewModal(true)
  }

  const openVersionHistory = async (file) => {
    setVersionFile(file)
    try {
      const res = await fileService.getVersionHistory(file.id)
      if (res.success) {
        setVersions(res.data.versions || [])
      }
    } catch (err) {
      console.error('Error fetching versions:', err)
    }
    setShowVersionModal(true)
  }

  const handleReviewComplete = () => {
    setShowReviewModal(false)
    setReviewFile(null)
    fetchFiles()
  }

  // Filter groups by search
  const filteredGroups = groups.filter(g => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return g.judul?.toLowerCase().includes(q) ||
           g.spk_id?.toLowerCase().includes(q) ||
           g.kode?.toLowerCase().includes(q)
  })

  const tabs = [
    { key: 'pending', label: 'Pending', icon: Clock, color: 'text-yellow-600' },
    { key: 'validated', label: 'Validated', icon: CheckCircle, color: 'text-green-600' },
    { key: 'rejected', label: 'Rejected', icon: XCircle, color: 'text-red-600' },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <span className="text-2xl">🔎</span>
          Review File
        </h1>
        <p className="text-gray-500">Validasi file sebelum masuk proses cetak</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-4 border-b">
        {tabs.map(tab => {
          const Icon = tab.icon
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.key
                  ? `border-blue-600 ${tab.color}`
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          )
        })}
        <div className="ml-auto text-sm text-gray-500">
          {totalFiles} file
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Cari SPK..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg"
        />
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">
            {activeTab === 'pending' ? 'Tidak ada file menunggu review' :
             activeTab === 'validated' ? 'Belum ada file yang divalidasi' :
             'Tidak ada file yang ditolak'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredGroups.map((group) => (
            <SPKGroup
              key={group.spk_id}
              group={group}
              activeTab={activeTab}
              onReview={openReviewModal}
              onDownload={handleDownload}
              onVersionHistory={openVersionHistory}
            />
          ))}
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && reviewFile && (
        <ReviewModal
          file={reviewFile}
          onClose={() => { setShowReviewModal(false); setReviewFile(null) }}
          onComplete={handleReviewComplete}
        />
      )}

      {/* Version History Modal */}
      {showVersionModal && versionFile && (
        <VersionHistoryModal
          file={versionFile}
          versions={versions}
          onClose={() => { setShowVersionModal(false); setVersionFile(null); setVersions([]) }}
        />
      )}
    </div>
  )
}

// SPK Group Card with proper table
function SPKGroup({ group, activeTab, onReview, onDownload, onVersionHistory }) {
  const [expanded, setExpanded] = useState(true)

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })
  }

  const formatSize = (size) => {
    if (!size || typeof size !== 'number') return '-'
    if (size < 1048576) return (size / 1024).toFixed(0) + ' KB'
    return (size / 1048576).toFixed(1) + ' MB'
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      {/* SPK Header */}
      <div
        className="px-4 py-3 border-b cursor-pointer hover:bg-gray-50 flex items-center justify-between"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <span className={`px-2 py-1 text-xs font-bold rounded border ${PRIORITY_STYLES[group.priority] || PRIORITY_STYLES.normal}`}>
            {group.priority?.toUpperCase()}
          </span>
          <div>
            <h3 className="font-bold text-gray-800">{group.judul}</h3>
            <p className="text-sm text-gray-500">
              {group.spk_id} | {group.kode} | {group.qty?.toLocaleString()} eks
              {group.deadline && ` | Deadline: ${formatDate(group.deadline)}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{group.files.length} file</span>
          {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </div>
      </div>

      {/* Files Table */}
      {expanded && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-xs uppercase">
                <th className="px-4 py-2 text-left font-medium w-28">Tipe</th>
                <th className="px-4 py-2 text-left font-medium w-20">Sekolah</th>
                <th className="px-4 py-2 text-left font-medium">Info File</th>
                <th className="px-4 py-2 text-left font-medium w-28">Uploader</th>
                <th className="px-4 py-2 text-right font-medium w-40">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {group.files.map((file) => (
                <tr key={file.id} className="hover:bg-gray-50">
                  {/* Tipe */}
                  <td className="px-4 py-2.5">
                    <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded ${FILE_TYPE_COLORS[file.file_type] || 'bg-gray-100 text-gray-700'}`}>
                      {FILE_TYPE_LABELS[file.file_type] || file.file_type}
                    </span>
                  </td>
                  {/* Sekolah */}
                  <td className="px-4 py-2.5 text-gray-600">
                    {file.school_code || '-'}
                  </td>
                  {/* Info */}
                  <td className="px-4 py-2.5">
                    <div>
                      <span className="text-gray-700">v{file.version || 1}</span>
                      <span className="text-gray-400 mx-1.5">&middot;</span>
                      <span className="text-gray-500">{formatSize(file.file_size)}</span>
                      {file.pages && (
                        <>
                          <span className="text-gray-400 mx-1.5">&middot;</span>
                          <span className="text-gray-500">{file.pages} hal</span>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 truncate max-w-xs" title={file.filename}>{file.filename}</p>
                    {file.status === 'rejected' && file.reject_reason && (
                      <p className="text-xs text-red-600 mt-0.5 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{file.reject_reason}</span>
                      </p>
                    )}
                  </td>
                  {/* Uploader */}
                  <td className="px-4 py-2.5">
                    <p className="text-gray-700 text-xs">{file.uploaded_by_name || '-'}</p>
                    <p className="text-gray-400 text-xs">{formatDate(file.uploaded_at)}</p>
                  </td>
                  {/* Aksi */}
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5 justify-end">
                      {(file.version || 1) > 1 && (
                        <button
                          onClick={() => onVersionHistory(file)}
                          className="px-2 py-1 text-xs text-purple-600 bg-purple-50 rounded hover:bg-purple-100"
                          title="Histori versi"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => onDownload(file)}
                        className="px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                        title="Download"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      {activeTab === 'pending' && (
                        <>
                          <button
                            onClick={() => onReview(file)}
                            className="px-2.5 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200"
                          >
                            Validasi
                          </button>
                          <button
                            onClick={() => onReview(file)}
                            className="px-2.5 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200"
                          >
                            Tolak
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// Review Modal
function ReviewModal({ file, onClose, onComplete }) {
  const [qcChecklist, setQcChecklist] = useState({})
  const [templates, setTemplates] = useState([])
  const [rejectReason, setRejectReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    loadTemplates()
  }, [])

  const loadTemplates = async () => {
    try {
      const res = await fileService.getQCTemplates()
      if (res.success) {
        const items = res.data.templates[file.file_type] || []
        setTemplates(items)
        // Initialize checklist
        const initial = {}
        items.forEach(item => { initial[item.id] = false })
        setQcChecklist(initial)
      }
    } catch (err) {
      console.error('Error loading templates:', err)
    }
  }

  const toggleCheck = (id) => {
    setQcChecklist(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const allChecked = templates.length > 0 && templates.every(t => qcChecklist[t.id])

  const handleSubmit = async (action) => {
    setError('')

    if (action === 'rejected' && !rejectReason.trim()) {
      setError('Alasan penolakan harus diisi')
      return
    }

    setSubmitting(true)
    try {
      const payload = {
        status: action,
        validationData: qcChecklist,
      }
      if (action === 'rejected') {
        payload.reject_reason = rejectReason.trim()
      }

      const res = await fileService.validate(file.id, payload)
      if (res.success) {
        onComplete()
      } else {
        setError(res.message || 'Gagal menyimpan review')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menyimpan review')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <span className="text-xl">🔎</span>
            Review File
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* File Info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="font-medium text-gray-800">{file.filename}</p>
            <p className="text-sm text-gray-500 mt-1">
              <span className={`px-1.5 py-0.5 text-xs font-bold rounded ${FILE_TYPE_COLORS[file.file_type]}`}>
                {FILE_TYPE_LABELS[file.file_type]}
              </span>
              {file.school_code && <span className="ml-2">{file.school_code}</span>}
              <span className="ml-2">v{file.version || 1}</span>
              {file.pages && <span className="ml-2">{file.pages} halaman</span>}
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          {/* QC Checklist */}
          {templates.length > 0 && (
            <div>
              <h3 className="font-medium text-gray-800 mb-2">QC Checklist</h3>
              <div className="space-y-2">
                {templates.map((item) => (
                  <label
                    key={item.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      qcChecklist[item.id]
                        ? 'bg-green-50 border-green-200'
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={qcChecklist[item.id] || false}
                      onChange={() => toggleCheck(item.id)}
                      className="w-4 h-4 text-green-600 rounded"
                    />
                    <span className={`text-sm ${qcChecklist[item.id] ? 'text-green-700' : 'text-gray-700'}`}>
                      {item.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Reject Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Alasan Penolakan (wajib jika tolak)
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              placeholder="Contoh: Halaman 45 gambar blur, footer salah format..."
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => handleSubmit('validated')}
              disabled={submitting || !allChecked}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
            >
              <CheckCircle className="w-4 h-4" />
              {submitting ? 'Menyimpan...' : 'Validasi'}
            </button>
            <button
              onClick={() => handleSubmit('rejected')}
              disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
            >
              <XCircle className="w-4 h-4" />
              {submitting ? 'Menyimpan...' : 'Tolak'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Version History Modal
function VersionHistoryModal({ file, versions, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-bold">Histori Versi</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          <p className="text-sm text-gray-500 mb-4">
            {FILE_TYPE_LABELS[file.file_type]} - {file.school_code || 'Reguler'}
          </p>

          <div className="space-y-3">
            {versions.map((v, idx) => (
              <div
                key={v.id}
                className={`p-3 rounded-lg border ${
                  v.is_latest ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-800">v{v.version}</span>
                    {v.is_latest && (
                      <span className="px-1.5 py-0.5 text-xs bg-blue-200 text-blue-800 rounded font-medium">
                        Latest
                      </span>
                    )}
                    <span className={`px-1.5 py-0.5 text-xs rounded font-medium ${
                      v.status === 'validated' ? 'bg-green-100 text-green-700' :
                      v.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {v.status?.toUpperCase()}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {v.uploaded_at ? new Date(v.uploaded_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{v.filename}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Upload: {v.uploaded_by_name || '-'}
                  {v.validated_by_name && ` | Review: ${v.validated_by_name}`}
                </p>
                {v.reject_reason && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {v.reject_reason}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ReviewerPage
